import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome, FiFolder, FiUsers, FiUserCheck,
  FiBriefcase, FiLogOut, FiChevronLeft, FiChevronRight,
  FiBook, FiLink, FiSettings, FiFileText, FiMenu, FiShield,
  FiChevronDown
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const menuItems = [
  { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
  { path: '/proforma-invoices', icon: FiFileText, label: 'Proforma Invoice', permission: { module: 'document', action: 'read' } },
  { path: '/commercial-invoices', icon: FiFolder, label: 'Commercial Invoices', permission: { module: 'document', action: 'read' } },
  { path: '/buyers', icon: FiUserCheck, label: 'Buyer Details', permission: { module: 'document', action: 'read' } },
  { path: '/companies', icon: FiBriefcase, label: 'Company Info', permission: { module: 'company', action: 'read' } },
  { path: '/contacts', icon: FiBook, label: 'Contacts', permission: { module: 'contact', action: 'read' } },
  { path: '/settings', icon: FiSettings, label: 'Settings' },
  {
    icon: FiUsers, label: 'User Management', permission: { module: 'user', action: 'read' },
    children: [
      { path: '/roles-permissions', icon: FiShield, label: 'Roles & Permissions', superAdminOnly: true },
      { path: '/users', icon: FiUsers, label: 'Users', permission: { module: 'user', action: 'read' } },
    ],
  },
  { path: '/references', icon: FiLink, label: 'Reference Links' },
];

export default function Sidebar({ collapsed: collapsedProp, onToggle, isMobile, mobileOpen, onCloseMobile }) {
  const [collapsedLocal, setCollapsedLocal] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedLocal;
  const { user, activeCompany, logout, hasPermission, isSuperAdmin } = useAuth();

  const isItemVisible = (item) => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (!item.permission) return true;
    return hasPermission(item.permission.module, item.permission.action);
  };

  const visibleMenuItems = menuItems.filter(item => {
    if (item.children) {
      return item.children.some(child => isItemVisible(child));
    }
    return isItemVisible(item);
  });

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // On mobile, hide sidebar off-screen unless mobileOpen
  const sidebarVisible = isMobile ? mobileOpen : true;
  const sidebarWidth = isMobile ? 260 : (collapsed ? 72 : 260);

  // Close mobile sidebar on navigation
  const handleNavClick = () => {
    if (isMobile && onCloseMobile) onCloseMobile();
  };

  const handleToggle = () => {
    if (onToggle) onToggle();
    else setCollapsedLocal(!collapsedLocal);
  };

  return (
    <motion.aside
      animate={{
        width: sidebarWidth,
        x: isMobile && !mobileOpen ? -280 : 0,
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, #064e3b 0%, #047857 50%, #059669 100%)',
        zIndex: isMobile ? 200 : 100,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: sidebarVisible ? '4px 0 20px rgba(6, 78, 59, 0.3)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Logo Area */}
      <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <AnimatePresence mode="wait">
          {(!collapsed || isMobile) ? (
            <motion.div
              key="full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src="/ems-logo.png"
                  alt="EMS"
                  style={{
                    width: 38, height: 38, objectFit: 'contain',
                    borderRadius: 8,
                    filter: 'drop-shadow(0 2px 8px rgba(52, 211, 153, 0.3))',
                  }}
                />
                <div>
                  <div style={{ color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'Poppins, sans-serif' }}>
                    Export Software
                  </div>
                  <div style={{ color: 'rgba(167, 243, 208, 0.8)', fontSize: 10 }}>Management System</div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="mini"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ textAlign: 'center' }}
            >
              <img
                src="/ems-logo.png"
                alt="EMS"
                style={{
                  width: 38, height: 38, objectFit: 'contain',
                  margin: '0 auto', display: 'block',
                  borderRadius: 8,
                  filter: 'drop-shadow(0 2px 8px rgba(52, 211, 153, 0.3))',
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Active Company */}
      {activeCompany && (!collapsed || isMobile) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            margin: '12px 12px 0',
            padding: '10px 12px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 10,
            fontSize: 12,
            color: '#a7f3d0',
          }}
        >
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, color: 'rgba(167,243,208,0.6)' }}>
            Active Company
          </div>
          <div style={{ fontWeight: 600, color: 'white', fontSize: 13 }}>
            {activeCompany.exporter_name || 'No Company'}
          </div>
        </motion.div>
      )}

      {/* Navigation */}
      <nav style={{ flex: 1, padding: '16px 8px', overflowY: 'auto' }}>
        {visibleMenuItems.map((item) => {
          if (item.children) {
            const visibleChildren = item.children.filter(isItemVisible);
            if (visibleChildren.length === 0) return null;
            const isExpanded = !!expandedGroups[item.label];
            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleGroup(item.label)}
                  title={collapsed && !isMobile ? item.label : ''}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: collapsed && !isMobile ? '12px' : '12px 16px',
                    marginBottom: 4,
                    borderRadius: 10,
                    border: 'none',
                    cursor: 'pointer',
                    color: 'rgba(167, 243, 208, 0.8)',
                    background: isExpanded ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                    fontWeight: 400,
                    fontSize: 14,
                    transition: 'all 0.2s ease',
                    justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                    fontFamily: 'inherit',
                  }}
                >
                  <item.icon size={20} />
                  {(!collapsed || isMobile) && (
                    <>
                      <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                      <motion.span
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <FiChevronDown size={14} />
                      </motion.span>
                    </>
                  )}
                </button>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: 'hidden' }}
                    >
                      {visibleChildren.map((child) => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          onClick={handleNavClick}
                          style={({ isActive }) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: collapsed && !isMobile ? '10px' : '10px 16px 10px 36px',
                            marginBottom: 2,
                            borderRadius: 10,
                            textDecoration: 'none',
                            color: isActive ? '#ffffff' : 'rgba(167, 243, 208, 0.7)',
                            background: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                            fontWeight: isActive ? 600 : 400,
                            fontSize: 13,
                            transition: 'all 0.2s ease',
                            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                          })}
                          title={collapsed && !isMobile ? child.label : ''}
                        >
                          <child.icon size={16} />
                          {(!collapsed || isMobile) && <span>{child.label}</span>}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: collapsed && !isMobile ? '12px' : '12px 16px',
                marginBottom: 4,
                borderRadius: 10,
                textDecoration: 'none',
                color: isActive ? '#ffffff' : 'rgba(167, 243, 208, 0.8)',
                background: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                fontWeight: isActive ? 600 : 400,
                fontSize: 14,
                transition: 'all 0.2s ease',
                justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                position: 'relative',
              })}
              title={collapsed && !isMobile ? item.label : ''}
            >
              <item.icon size={20} />
              {(!collapsed || isMobile) && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  {item.label}
                </motion.span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        {(!collapsed || isMobile) && user && (
          <div style={{
            padding: '10px 12px', marginBottom: 8,
            color: 'rgba(167,243,208,0.8)', fontSize: 13,
          }}>
            <div style={{ fontWeight: 600, color: 'white' }}>{user.full_name}</div>
            <div style={{ fontSize: 11 }}>{user.email}</div>
          </div>
        )}

        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%',
            padding: (collapsed && !isMobile) ? '12px' : '12px 16px',
            border: 'none', borderRadius: 10, cursor: 'pointer',
            background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5',
            fontSize: 14, fontWeight: 500,
            justifyContent: (collapsed && !isMobile) ? 'center' : 'flex-start',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => e.target.style.background = 'rgba(239, 68, 68, 0.25)'}
          onMouseLeave={e => e.target.style.background = 'rgba(239, 68, 68, 0.15)'}
        >
          <FiLogOut size={18} />
          {(!collapsed || isMobile) && 'Logout'}
        </button>

        {!isMobile && (
          <button
            onClick={handleToggle}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', marginTop: 8, padding: '12px',
              border: '1px solid rgba(167,243,208,0.2)',
              borderRadius: 10, cursor: 'pointer',
              background: 'rgba(255,255,255,0.08)',
              color: '#a7f3d0',
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.borderColor = 'rgba(167,243,208,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.borderColor = 'rgba(167,243,208,0.2)';
            }}
          >
            <FiMenu size={20} />
          </button>
        )}
      </div>
    </motion.aside>
  );
}
